/**
 * The plays of a book, for the person who set it.
 *
 * A learner's results are posted to the AppView by `createFlashbookPlay`; this is
 * the other end of that wire. One subject — a book — asked across *all* its
 * learners, which is the only way a class report can exist at all, and the reason
 * it is a server question rather than a device one: a teacher's phone has never
 * seen a learner's answers.
 *
 * Two details that are easy to get wrong and fail silently, both inherited from
 * `useActorFlashbookDraftsQuery`, which solves the same problem:
 *
 *  • **The host is the AppView, and it is called through `fetch`.** The endpoint is
 *    a Taderok extension that is not among the custom lexicons the frontend
 *    registers, so an `agent.app.bsky.feed.getFlashbookPlays` call would throw for
 *    an unknown method rather than reporting anything useful.
 *  • **A deployment that has not been taught this method is not an error.** It
 *    answers 501 or 404, and both are reported as *no plays* so the class report
 *    says "no one has played this yet" instead of throwing at a teacher. The
 *    feature switches itself on when the backend that implements the handler is
 *    deployed.
 */
import {useQuery} from '@tanstack/react-query'

import {PUBLIC_BSKY_SERVICE} from '#/lib/constants'
import {type ClassPlay} from '#/lib/flashbookClassReport'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

const flashbookPlaysQueryKeyRoot = 'flashbookPlays'

export const createFlashbookPlaysQueryKey = (flashbookUri: string) =>
  createQueryKey(flashbookPlaysQueryKeyRoot, {flashbookUri})

/** One play as the AppView lists it, before it has been cleaned up. */
type PlayView = {
  learnerDid?: unknown
  learnerHandle?: unknown
  learnerName?: unknown
  score?: unknown
  timeMs?: unknown
  /** When it was played, used only to order a learner's own plays. */
  at?: unknown
  pageResults?: unknown
}

/**
 * A play the model can read, or `null` for one it cannot.
 *
 * A row without a learner cannot be attributed to anybody, and a class report that
 * printed one as "someone" would be less useful than the same report without it —
 * so it is dropped rather than guessed at. `pageResults` is passed through as the
 * shared result type: the model walks the *book* and looks results up, so a
 * malformed extra block is ignored there and this does not have to validate it a
 * second time.
 */
function classPlayOf(view: PlayView): ClassPlay | null {
  const did = typeof view.learnerDid === 'string' ? view.learnerDid.trim() : ''
  if (!did) return null
  return {
    learnerDid: did,
    learnerHandle:
      typeof view.learnerHandle === 'string' ? view.learnerHandle : undefined,
    learnerName:
      typeof view.learnerName === 'string' ? view.learnerName : undefined,
    score: typeof view.score === 'number' ? view.score : undefined,
    timeMs: typeof view.timeMs === 'number' ? view.timeMs : undefined,
    pageResults: Array.isArray(view.pageResults)
      ? (view.pageResults as ClassPlay['pageResults'])
      : [],
  }
}

/**
 * A learner's timestamp, as a sortable number.
 *
 * The model is told to read the list as time order, so somebody has to establish
 * it. Sorting here rather than in the model keeps the timestamp parsing — and the
 * knowledge that the server dates its rows — in one place next to the wire, instead
 * of making the arithmetic deal with ISO strings. Rows without a date keep the
 * order the server sent them, which is the best anyone can do with no time.
 */
function playedAtOf(view: PlayView): number {
  if (typeof view.at !== 'string') return Number.NaN
  const at = Date.parse(view.at)
  return Number.isNaN(at) ? Number.NaN : at
}

/**
 * The plays of a book, oldest first.
 *
 * `enabled` is driven by the URI, so a surface that only sometimes shows a class
 * report fetches nothing until it does.
 */
export function useFlashbookClassPlaysQuery(flashbookUri: string | undefined) {
  const agent = useAgent()

  return useQuery({
    queryKey: createFlashbookPlaysQueryKey(flashbookUri ?? ''),
    enabled: !!flashbookUri,
    queryFn: async (): Promise<ClassPlay[]> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const params = new URLSearchParams({
        flashbookUri: flashbookUri!,
        limit: '100',
      })
      const res = await fetch(
        `${PUBLIC_BSKY_SERVICE}/xrpc/app.bsky.feed.getFlashbookPlays?${params}`,
        {headers: {Authorization: `Bearer ${accessJwt}`}},
      )
      if (!res.ok) {
        // 501 = the method exists but is not implemented; 404 = the server has
        // never heard of it. Neither is worth a retry storm or a console error —
        // report "no plays", which the report already knows how to say.
        if (res.status === 501 || res.status === 404) return []
        const err = (await res.json().catch(() => ({}))) as {message?: string}
        throw new Error(err.message ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {plays?: PlayView[]}
      const rows = data.plays ?? []

      const dated = rows.map((view, index) => ({
        view,
        index,
        at: playedAtOf(view),
      }))
      dated.sort((a, b) => {
        if (Number.isNaN(a.at) || Number.isNaN(b.at)) return a.index - b.index
        return a.at - b.at
      })

      return dated
        .map(({view}) => classPlayOf(view))
        .filter((play): play is ClassPlay => play !== null)
    },
    // Short: a class that has just been set the book comes back with new plays
    // during the lesson, and a stale roster is worse than a small refetch.
    staleTime: 1000 * 30,
  })
}
